/**
 * POS queue order-status filtering.
 *
 * WHY THIS IS A SHARED HELPER RATHER THAN AN INLINE PREDICATE. The rule was written inline
 * on each surface and the copies drifted. The mobile app ended up with TWO versions in one
 * file: the real list filter, which handled `active` / `open` / `cancelled`, and a second
 * copy used only for the "Loaded N of M order(s)" status line, which did nothing but an
 * exact label match. With ACTIVE selected the second copy compared labels against the
 * literal string "ACTIVE", matched nothing, and reported zero while the list below rendered
 * correctly. One tested function removes the whole class of problem.
 *
 * THE SELECTION VOCABULARY. Three selections are server buckets and the rest are client-side
 * narrowings of a full fetch:
 *
 *   all        - everything the query returned, cancellations included
 *   active     - not cancelled and NOT FINISHED
 *   cancelled  - only cancellations
 *   open       - active and not yet progressed past OPEN
 *   <label>    - exact fulfillment label, e.g. "DELIVERED", "PICKUP COMPLETE"
 *
 * `active` EXCLUDING FINISHED ORDERS IS THE POINT OF THIS FILE. It previously meant only
 * "not cancelled", so an order already handed to the customer sat in the queue beside orders
 * still being cooked. The server-side `active` bucket enforces the same rule now, which is
 * what keeps a paginated fetch from spending its row budget on finished orders; this is the
 * display-side half of that fix.
 */

/**
 * Formatted fulfillment labels that mean the order is finished.
 *
 * Deliberately expressed as FORMATTED labels, not raw stored values. `formatFulfillmentStatus`
 * has already collapsed the many spellings (COMPLETED, PICKUP_COMPLETE, PICKED UP, ...) and
 * resolved the delivery-vs-pickup split, where the same raw `COMPLETED` becomes `DELIVERED`
 * for a delivery and `PICKUP COMPLETE` for a pickup. Testing the label means not having to
 * know which kind of order it was.
 */
export const TERMINAL_FULFILLMENT_LABELS = ['DELIVERED', 'PICKUP COMPLETE', 'DINE-IN COMPLETE'];

/** True when a formatted fulfillment label represents a finished order. */
export function isTerminalFulfillmentLabel(label) {
  return TERMINAL_FULFILLMENT_LABELS.includes(String(label || '').trim().toUpperCase());
}

/**
 * isHeldScheduledOrder({ isScheduled, scheduledTime, posReleaseMinutes, now }) -> boolean
 *
 * A scheduled order is HELD (kept out of the live "active" queue) until
 * `posReleaseMinutes` before its slot, then released so the kitchen works it like a normal
 * incoming ticket (scheduled-orders spec R6.1/R6.2). This is the client half of the hybrid
 * timing: the order was captured and paid immediately, but staff should not see it in the
 * cook-now queue hours early.
 *
 * FAILS CLOSED. Anything that is not a genuine scheduled order with a usable future time ->
 * false ("not held", i.e. a normal order). That covers an ASAP order (no `isScheduled`), a
 * record missing `scheduledTime`, a tenant whose queue response carried no release lead, and
 * any junk. The whole point is that an absent/garbled field can never HIDE an order from
 * staff -- the worst available failure -- and that an ASAP order's classification is exactly
 * what it is today (R6.4, R6.7).
 *
 * `scheduledTime` is a UTC epoch in SECONDS (what the order record stores). `now` is injected
 * (epoch seconds) for testability and defaults to the real clock.
 */
export function isHeldScheduledOrder({
  isScheduled,
  scheduledTime,
  posReleaseMinutes = 0,
  now = Math.floor(Date.now() / 1000),
} = {}) {
  if (isScheduled !== true) return false;
  const sched = Number(scheduledTime);
  if (!Number.isFinite(sched)) return false;
  const releaseLead = Number(posReleaseMinutes);
  const lead = Number.isFinite(releaseLead) && releaseLead > 0 ? releaseLead : 0;
  const releaseInstant = sched - lead * 60;
  const nowS = Number(now);
  if (!Number.isFinite(nowS)) return false;
  return nowS < releaseInstant;
}

/**
 * matchesOrderStatusFilter({ selection, fulfillmentLabel, isCancelled }) -> boolean
 *
 * Takes the ALREADY-FORMATTED label rather than the order, so the two surfaces can each use
 * their own `formatFulfillmentStatus` (byte-identical logic, different module homes) and this
 * stays a pure string-in / boolean-out function with no import of either.
 *
 * `isCancelled` is passed in rather than derived because the web surface treats an order
 * whose `status` is CANCELLED as cancelled even when its fulfillment label is not, and that
 * pre-existing breadth is worth preserving exactly.
 *
 * An EMPTY label means "no fulfillment progress recorded", which is an OPEN order, not an
 * unknown one. That is why `open` accepts it and `active` keeps it.
 */
export function matchesOrderStatusFilter({
  selection,
  fulfillmentLabel,
  isCancelled = false,
  isScheduled = false,
  scheduledTime = null,
  posReleaseMinutes = 0,
  now = Math.floor(Date.now() / 1000),
}) {
  const rawSelection = String(selection == null ? '' : selection).trim();
  const key = rawSelection.toLowerCase();

  if (!key || key === 'all') return true;

  if (key === 'cancelled' || key === 'canceled' || key === 'cancel') {
    return Boolean(isCancelled);
  }

  // Every remaining selection is a view of non-cancelled orders.
  if (isCancelled) return false;

  const label = String(fulfillmentLabel || '').trim().toUpperCase();

  // Is this order currently HELD (scheduled for later, not yet within the release lead)?
  // Computed once and reused by both the `scheduled` bucket and the `active` exclusion so
  // the two can never disagree. For an ASAP order this is always false and nothing below
  // changes from today's behavior (scheduled-orders spec R6.4).
  const held = isHeldScheduledOrder({ isScheduled, scheduledTime, posReleaseMinutes, now });

  // The explicit "Scheduled / Upcoming" view: held scheduled orders, regardless of their
  // fulfillment progress, so staff can always see what is coming (R6.3).
  if (key === 'scheduled' || key === 'upcoming') {
    return held;
  }

  if (key === 'active') {
    // A held scheduled order is NOT in the live active queue yet; once released
    // (held === false) it behaves exactly like any other active order (R6.1/R6.2).
    if (held) return false;
    return !isTerminalFulfillmentLabel(label);
  }

  if (key === 'open') {
    // A held scheduled order is OPEN by status (no fulfillment progress yet), but it must
    // not surface in the OPEN working view either -- it belongs only under
    // Scheduled/Upcoming until release. Without this guard a scheduled order that is
    // correctly hidden from ACTIVE still shows under OPEN, which is the inconsistency this
    // closes. ASAP orders are unaffected (held is always false for them).
    if (held) return false;
    return !label || label === 'OPEN';
  }

  // Any remaining working-view selection (an exact fulfillment-label match) must also
  // exclude a still-held scheduled order, for the same reason OPEN and ACTIVE do: it is
  // not yet something staff should see outside the Scheduled/Upcoming view.
  if (held) return false;

  // Exact label match. An order with no recorded progress is OPEN, so compare against the
  // effective label rather than the empty string — otherwise selecting OPEN by its label
  // spelling would disagree with selecting it by the `open` key above.
  return (label || 'OPEN') === rawSelection.toUpperCase();
}

export default matchesOrderStatusFilter;

/**
 * formatQueueLoadLine({ shown, fetched, hostLabel, stoppedEarly }) -> string
 *
 * The POS queue "Loaded N order(s)" banner text.
 *
 * WHY THIS IS A FUNCTION AND WHY `shown` IS AN ARGUMENT. Both surfaces used to build this
 * string inside the fetch, from the size of the array the SERVER returned. The list beneath
 * renders that array AFTER the client-side status / search / today narrowing, so with any
 * filter active the banner reported a number that matched nothing on screen -- observed on
 * web as "Loaded 18 order(s)" above two listed DINE-IN COMPLETE tickets.
 *
 * Mobile had already moved to "N of M" but computed N from a SECOND copy of the list
 * predicate, which drifted: it passed no scheduled-order fields, so a held scheduled order
 * counted as shown while the list correctly hid it, and it had no ecomm branch.
 *
 * Both callers now pass `shown` straight from the array they map into rows, so the number is
 * equal to the rows by construction rather than by two predicates agreeing. `fetched` is
 * only mentioned when it differs, so the common unfiltered case stays short.
 */
export function formatQueueLoadLine({ shown, fetched, hostLabel = '', stoppedEarly = false } = {}) {
  const shownN = Number.isFinite(Number(shown)) ? Math.max(0, Number(shown)) : 0;
  const fetchedRaw = Number(fetched);
  const fetchedN = Number.isFinite(fetchedRaw) ? Math.max(0, fetchedRaw) : shownN;
  const host = String(hostLabel || '').trim();
  const from = host ? ` from ${host}` : '';
  const more = stoppedEarly ? ' (More available. Narrow filters or use Load more.)' : '';

  // WHY NOT "Loaded 7 of 18". That phrasing was the first attempt and it reads as a
  // puzzle: two bare numbers with no stated relationship, which an operator reasonably
  // reads as a partial or failed load ("7 or 18 — what is this?"). The second number is
  // only meaningful if we say what it MEANS, so name the gap instead of printing the
  // total. "Showing" also matches AdminOrdersQueue, which already distinguishes what is
  // on screen from what was fetched.
  const hidden = Math.max(0, fetchedN - shownN);
  const because = hidden > 0 ? ` · ${hidden} hidden by filters` : '';
  return `Showing ${shownN} order(s)${from}${because}.${more}`;
}
